import { Query, QueryKey, useMutation, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';

import { handleErrors, parseAsJson } from '../../utils/requestHelpers';
import fetch from '../../utils/fetchService';

import Config from '../../config';
import { METHODS } from '../constants';

export type OnSuccessCallback<TData, TVariables> = (data: TData, variables: TVariables) => void;

type CustomMutationOptions<TData, TError, TVariables> = {
  invalidateKey: QueryKey | ((query: Query) => boolean),
  resetKey?: ((query: Query) => boolean) | string,
  errorMessage: string,
  successMessage?: string,
  onMutate?: (variables: TVariables) => void,
  onSuccess?: OnSuccessCallback<TData, TVariables>,
  onError?: (error: TError, variables: TVariables) => void,
  handleData: (variables: TVariables) => {url: string, method: METHODS, body?: TVariables},
  parseResponseAsJson?: boolean,
  parseResponseErrorMessage?: boolean,
}

type FetchingError = {
  isFetchingError: boolean,
  message: string,
} & Error;


type AuthorizationError = {
  isAuthorizationError: boolean,
  message: string,
} & Error;

type QueryError = Error & (FetchingError | AuthorizationError);

const isAuthorizationError = (error: Error | QueryError): error is AuthorizationError =>
  typeof (error as AuthorizationError).isAuthorizationError !== 'undefined'
  && typeof (error as AuthorizationError).message !== 'undefined';

const isFetchingError = (error: Error | QueryError): error is FetchingError =>
  typeof (error as FetchingError).isFetchingError !== 'undefined'
  && typeof (error as QueryError).message !== 'undefined';

export const useCustomMutation = <
  TData,
  TError extends Error | QueryError = QueryError,
  TVariables extends unknown = unknown
>({
    invalidateKey,
    resetKey,
    errorMessage,
    successMessage,
    onSuccess,
    onError,
    onMutate,
    handleData,
    parseResponseAsJson = false,
    parseResponseErrorMessage = false,
  }: CustomMutationOptions<TData, TError, TVariables>) => {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables>(
    (variables: TVariables) => {
      const { url, method, body } = handleData(variables);
      const result = fetch(url, { method, body: body && JSON.stringify(body) })
        .then(handleErrors({
          errorMessage,
          parseResponseErrorMessage,
        }));

      if (parseResponseAsJson) {
        return result.then(parseAsJson);
      }

      return result;
    },
    {
      onSuccess: (data, variables) => {
        if (invalidateKey) {
          if (typeof invalidateKey === 'function') {
            queryClient.invalidateQueries({ predicate: invalidateKey });
          } else {
            queryClient.invalidateQueries(invalidateKey, { exact: true });
          }
        }
        if (resetKey) {
          if (typeof resetKey === 'function') {
            queryClient.resetQueries({ predicate: resetKey });
          } else {
            queryClient.resetQueries(resetKey);
          }
        }
        if (onSuccess) {
          onSuccess(data, variables);
        }
        if (successMessage) {
          toastr.success('', successMessage, Config.defaultToastrSettings);
        }
      },
      onError: (error, variables) => {
        if (isAuthorizationError(error)) {
          return;
        }
        if (isFetchingError(error)) {
          toastr.error('', error.message, Config.defaultToastrSettings);
          return;
        }
        if (onError) {
          return onError(error, variables);
        }
        throw error;
      },
      onMutate,
    }
  );
};
